import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { MessageCircle, Send, ArrowLeft, User, Bot, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Navbar from "../components/Navbar";

const AIChatPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Add welcome message
    setMessages([{
      role: "assistant",
      content: `Hello ${user?.name}! I'm your AI interior design consultant at ARKY World. I can help you with:

• Interior design advice and style recommendations
• Product suggestions based on your needs
• Room layout and colour scheme ideas
• Eco-sustainable options

What would you like help with today?`
    }]);
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai/chat`,
        {
          message: userMessage,
          session_id: sessionId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
      
      if (response.data.recommendations?.length > 0) {
        setRecommendations(response.data.recommendations);
      }
    } catch (error) {
      toast.error("Failed to get response");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm sorry, I encountered an error. Please try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedQuestions = [
    "What colours work well in a small living room?",
    "Recommend eco-friendly furniture options",
    "How to create a cosy reading nook?",
    "What's trending in interior design?"
  ];

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex flex-col">
      {/* Navbar */}
      <Navbar />

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 md:px-12 py-8 flex flex-col">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#5C5D5C] hover:text-[#1A1B1A] mb-6"
        >
          <ArrowLeft size={20} /> Back
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-[#20C997] flex items-center justify-center">
            <MessageCircle className="text-white" size={24} />
          </div>
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A]">
              AI Design Consultant
            </h1>
            <p className="text-sm text-[#5C5D5C]">Get personalised interior design advice</p>
          </div>
        </div>

        <div className="flex-1 flex gap-6">
          {/* Chat Area */}
          <div className="flex-1 flex flex-col bg-white border border-[#EAE6DF] min-h-[500px]">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
              {messages.map((message, index) => (
                <div 
                  key={index}
                  className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${index}`}
                >
                  {message.role === "assistant" && (
                    <div className="w-8 h-8 rounded-full bg-[#20C997] flex items-center justify-center flex-shrink-0">
                      <Bot size={16} className="text-white" />
                    </div>
                  )}
                  <div className={`max-w-[80%] p-4 ${
                    message.role === "user" 
                      ? "bg-[#20C997] text-white" 
                      : "bg-[#F7F5F0] text-[#1A1B1A]"
                  }`}>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                  </div>
                  {message.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-[#F06570] flex items-center justify-center flex-shrink-0">
                      <User size={16} className="text-white" />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#20C997] flex items-center justify-center">
                    <Bot size={16} className="text-white" />
                  </div>
                  <div className="bg-[#F7F5F0] p-4">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#5C5D5C] rounded-full animate-bounce"></span>
                      <span className="w-2 h-2 bg-[#5C5D5C] rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></span>
                      <span className="w-2 h-2 bg-[#5C5D5C] rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggested Questions */}
            {messages.length <= 1 && (
              <div className="px-6 pb-4">
                <p className="text-xs text-[#8E8F8E] mb-2">Suggested questions:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((q, index) => (
                    <button
                      key={index}
                      onClick={() => setInput(q)}
                      className="text-xs px-3 py-1.5 bg-[#F7F5F0] text-[#5C5D5C] hover:bg-[#EFECE5] transition-colors"
                      data-testid={`suggested-${index}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-[#EAE6DF]">
              <div className="flex gap-3">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 rounded-none border-[#EAE6DF]"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none px-6"
                  data-testid="send-btn"
                >
                  <Send size={18} />
                </Button>
              </div>
            </div>
          </div>

          {/* Recommendations Sidebar */}
          {recommendations.length > 0 && (
            <div className="w-72 flex-shrink-0 hidden lg:block" data-testid="recommendations-sidebar">
              <div className="dashboard-card">
                <h3 className="font-medium text-[#1A1B1A] mb-4">Recommended Products</h3>
                <div className="space-y-3">
                  {recommendations.map((product) => (
                    <div 
                      key={product.id}
                      className="p-3 bg-[#F7F5F0] cursor-pointer hover:bg-[#EFECE5] transition-colors"
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      {product.image && (
                        <img src={product.image} alt={product.name} className="w-full h-24 object-cover mb-2" />
                      )}
                      <p className="font-medium text-[#1A1B1A] text-sm">{product.name}</p>
                      <p className="text-sm text-[#5C5D5C]">£{product.price}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;
